import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { unsubscribeFromPush } from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

export const MAX_TEARDOWN_ATTEMPTS_PER_SESSION = 3;

/**
 * Delay before an in-session teardown retry after a failed backend delete. A
 * failed delete leaves the device reachable by BOTH push pipelines (duplicate
 * notifications) until it succeeds, so we retry within the session (bounded by
 * MAX_TEARDOWN_ATTEMPTS_PER_SESSION) rather than waiting for the next full visit.
 */
export const TEARDOWN_RETRY_DELAY_MS = 30_000;

/**
 * Reduce a push endpoint to its origin for analytics. The full endpoint is a
 * per-device capability URL (long unique token) — a stable device identifier
 * that must not be shipped to third-party analytics. The origin keeps the
 * useful dimension (which push service handed off: fcm.googleapis.com,
 * web.push.apple.com, updates.push.services.mozilla.com, …) without the
 * sensitive part. Never throws: endpoints come from the browser and are valid
 * absolute URLs, but a malformed one must not break the teardown path.
 */
const pushEndpointOrigin = (endpoint: string): string => {
  try {
    return new URL(endpoint).origin;
  } catch {
    return 'invalid';
  }
};

/**
 * Hand this browser's device off the legacy VAPID pipeline once OneSignal owns
 * its push subscription.
 *
 * During the migration overlap the backend dual-sends (legacy VAPID + OneSignal),
 * so a device subscribed to BOTH would receive duplicate notifications. This hook
 * closes the legacy path per browser: after OneSignal's subscription is confirmed
 * (`oneSignalSubscribed === true`), it deletes the legacy backend subscription row
 * and unsubscribes the legacy browser subscription so the device is reachable by
 * exactly one path.
 *
 * The legacy VAPID subscription lives on the ROOT Workbox service-worker
 * registration (scope '/'). OneSignal's subscription lives on its own
 * '/push/onesignal/' registration and is never touched here — we look the legacy
 * sub up via `getRegistration('/')` specifically so we don't disturb OneSignal's.
 *
 * Ordering — backend row delete FIRST, then browser `unsubscribe()`: if the row
 * delete rejects we keep the browser subscription (and thus a retriable endpoint)
 * rather than orphaning a backend row the pipeline keeps dual-sending to. This is
 * the same rationale as the legacy unsubscribe mutation and
 * useRotateStaleVapidSubscription's cleanup.
 *
 * Structure mirrors useRotateStaleVapidSubscription: the backend call is wrapped
 * in a mutation (codebase rule: never call API functions directly), a
 * `cancelled` flag guards post-resolution effects against unmount, and a
 * per-session `attemptsRef` cap (`MAX_TEARDOWN_ATTEMPTS_PER_SESSION`) prevents an
 * infinite retry loop when the backend keeps failing. A `doneRef` latch stops the
 * effect re-running once teardown has succeeded (or found nothing to do), so a
 * healthy device tears down exactly once per session.
 *
 * In-session retry: a failed attempt — backend delete rejection OR the rarer
 * registration/subscription lookup rejection — schedules a bounded `setTimeout`
 * (`TEARDOWN_RETRY_DELAY_MS`) that bumps a `retryTick` state to re-run the effect.
 * Without it, every effect dep is stable after `oneSignalSubscribed` latches true,
 * so a transient failure would strand the device on BOTH pipelines (duplicate
 * notifications) until the next full page visit. The pending timer is cleared on
 * cleanup (unmount / re-run), and no retry is scheduled once the cap is reached.
 *
 * This is external-system synchronisation (service worker + backend), the one
 * sanctioned use of useEffect per the React patterns skill.
 *
 * The caller MUST gate `oneSignalSubscribed` on authentication too: the backend
 * DELETE (`unsubscribeFromPush`) requires an authenticated user, so passing
 * `true` for a signed-out device produces failing 401 DELETEs (up to the
 * per-session cap) and teardown-failed telemetry noise. Unauthed devices retry
 * on their next signed-in visit.
 */
export function useLegacyWebPushTeardown(oneSignalSubscribed: boolean): void {
  const { trackEvent } = useAmplitudeTracking();
  // Wrap the backend cleanup call in a mutation so it goes through the standard
  // TanStack Query layer (DevTools observability + a stable mutateAsync). We
  // don't read isPending/error/data — the effect drives ordering, the attempt
  // cap, and cancellation itself.
  const { mutateAsync: deleteBackendSubscription } = useMutation({
    mutationFn: ({ endpoint }: { endpoint: string }) => unsubscribeFromPush({ endpoint }),
  });
  // Per-session failed-attempt counter. Cleared implicitly by a page reload;
  // teardown is a once-per-device event so a small cap is plenty.
  const attemptsRef = useRef(0);
  // Latches once teardown has completed (success OR nothing-to-do) so a
  // re-render with the same `oneSignalSubscribed=true` doesn't re-run it.
  const doneRef = useRef(false);
  // Handle for a scheduled in-session retry so cleanup can cancel it (avoids a
  // post-unmount attempt / timer leak). Only one retry is ever pending at a time.
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped by the retry timer to re-run the effect after a failed backend delete.
  // All other effect deps are stable once `oneSignalSubscribed` latches true, so
  // this tick is the only in-session signal that re-arms teardown on failure.
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    // Wait until OneSignal actually owns the subscription before dismantling the
    // legacy one — otherwise a device could end up reachable by neither path.
    if (!oneSignalSubscribed) return;
    // Already handed over (or found nothing to hand over) this session.
    if (doneRef.current) return;
    // Backend keeps rejecting: stop retrying so we don't loop forever. Leave
    // doneRef false so a later mount (fresh session) can try again.
    if (attemptsRef.current >= MAX_TEARDOWN_ATTEMPTS_PER_SESSION) return;
    attemptsRef.current += 1;

    let cancelled = false;

    // Re-arm the effect after a failed attempt: every other dep is stable once
    // `oneSignalSubscribed` latches true, so bumping `retryTick` via a bounded
    // timer is the ONLY in-session path back into teardown. Shared by both
    // failure classes (backend DELETE reject, registration/subscription lookup
    // reject) — either one leaves the device reachable by BOTH pipelines
    // (duplicate notifications) until a retry succeeds. Never called after
    // cleanup (`cancelled` is checked first), and the cap check keeps the last
    // permitted attempt from scheduling a tick the effect guard would discard.
    const scheduleRetry = (): void => {
      if (attemptsRef.current < MAX_TEARDOWN_ATTEMPTS_PER_SESSION) {
        retryTimeoutRef.current = setTimeout(() => {
          setRetryTick(t => t + 1);
        }, TEARDOWN_RETRY_DELAY_MS);
      }
    };

    const teardown = async (): Promise<void> => {
      // The legacy sub lives on the ROOT registration; OneSignal's own
      // registration is intentionally excluded.
      const registration = await navigator.serviceWorker.getRegistration('/');
      const subscription = (await registration?.pushManager.getSubscription()) ?? null;

      // `cancelled` is mutated by the cleanup fn during these awaits; TS's flow
      // analysis can't see that, so it flags the LATER post-await checks as
      // always falsy (this first one it can't yet narrow). Same closure-mutation
      // false positive suppressed in useEventStream / location-input.
      if (cancelled) return;

      if (!subscription) {
        // Nothing to migrate on this browser — mark done so we don't keep
        // re-checking on every render.
        doneRef.current = true;
        return;
      }

      const { endpoint } = subscription;

      try {
        // Backend row FIRST so a failure here keeps the browser sub retriable.
        await deleteBackendSubscription({ endpoint });
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure mutation from cleanup
        if (cancelled) return;
        // A failed delete leaves the device reachable by BOTH pipelines
        // (duplicate-notification risk) until a retry succeeds — surface it.
        logger.error(
          'Web push: legacy backend teardown failed, keeping browser subscription for retry',
          sanitizeError(error),
        );
        trackEvent(ANALYTICS_EVENTS.web_push_migration_teardown_failed, sanitizeError(error));
        scheduleRetry();
        return;
      }

      // Backend row gone — now retire the browser subscription. A failure here
      // is non-fatal: the backend no longer targets this endpoint, so the worst
      // case is a dormant browser sub the push service eventually 410s.
      try {
        await subscription.unsubscribe();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure mutation from cleanup
        if (cancelled) return;
        logger.warn(
          'Web push: legacy browser unsubscribe failed after backend teardown',
          sanitizeError(error),
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure mutation from cleanup
      if (cancelled) return;
      doneRef.current = true;
      // Origin only — the raw endpoint is a per-device capability URL (see
      // pushEndpointOrigin) and must not reach third-party analytics.
      trackEvent(ANALYTICS_EVENTS.web_push_migrated_to_onesignal, {
        endpoint_origin: pushEndpointOrigin(endpoint),
      });
    };

    void teardown().catch((error: unknown) => {
      // Registration lookup / getSubscription rejection (rare). Same stakes as
      // a failed backend delete — the device stays reachable by BOTH pipelines
      // — so it gets the same bounded in-session retry and the same telemetry
      // (a plain re-render would NOT re-run this effect: its deps are stable).
      if (cancelled) return;
      logger.error('Web push: legacy teardown lookup failed', sanitizeError(error));
      trackEvent(ANALYTICS_EVENTS.web_push_migration_teardown_failed, sanitizeError(error));
      scheduleRetry();
    });

    return () => {
      cancelled = true;
      // Cancel any pending in-session retry so it can't fire after unmount (or
      // after a re-run supersedes it) — prevents a post-unmount attempt / leak.
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // `retryTick` re-arms the effect after a failed delete (see the retry timer);
    // the other deps are stable once `oneSignalSubscribed` latches true.
  }, [oneSignalSubscribed, deleteBackendSubscription, trackEvent, retryTick]);
}
